import { type HTMLAttributes, type ReactNode, type Ref, useMemo, useRef } from 'react';
import { type Cell, flexRender, type Header } from '@tanstack/react-table';
import { clsx } from 'clsx';
import { match } from 'ts-pattern';
import { sanitizeCSSVariables } from '../../../../util/style';
import { Typography, type TypographyProps } from '../../../typography';
import { TypographyProvider } from '../../../typography/context';
import type { TableColumnDef } from '../../util/column';
import { getSafeColumnId } from '../../util/general';
import styles from './td.module.scss';

export const baseCellTypographyProps = { lineHeight: '1rem', size: '0.875rem' } as const satisfies TypographyProps;

interface TDProps<T extends object> extends HTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
  cell?: Cell<T, unknown>;
  defaultCellRenderWhenEmpty?: ReactNode;
  header?: Header<T, unknown>;
  isAggregateCell?: boolean;
  isSubRowCell?: boolean;
  ref?: Ref<HTMLDivElement>;
  showSkeleton: boolean | undefined;
}

export function TD<TData extends object>({
  cell,
  children,
  className,
  defaultCellRenderWhenEmpty,
  header,
  isAggregateCell,
  isSubRowCell,
  ref,
  showSkeleton,
  style,
  ...rest
}: TDProps<TData>) {
  // 'use no memo' directive is required for now because react-table v8 is not compatible with React 19's compiler just yet
  'use no memo';

  const cellRef = useRef<HTMLTableCellElement | null>(null);

  const mergedStyles = useMemo(() => {
    const id = getSafeColumnId(cell?.column.id || header?.column?.id || '');

    return sanitizeCSSVariables({
      flex: `var(--ixf-table-column-${id}-size)`,
      maxWidth: `var(--ixf-table-column-${id}-max-size)`,
      minWidth: `var(--ixf-table-column-${id}-min-size)`,
      ...style,
    });
  }, [style, cell?.column.id, header?.column?.id]);
  const { align, isActionColumn, typography } = (cell?.column.columnDef || header?.column?.columnDef || {}) as TableColumnDef<TData>;
  const value = cell?.getValue();
  const renderWhenEmpty =
    isAggregateCell || isSubRowCell || showSkeleton
      ? null
      : (cell?.column?.columnDef as TableColumnDef<TData>)?.renderWhenEmpty !== undefined
        ? (cell?.column?.columnDef as TableColumnDef<TData>)?.renderWhenEmpty
        : defaultCellRenderWhenEmpty;

  const mergedTypographyProps: TypographyProps = useMemo(
    () => ({
      ...baseCellTypographyProps,
      align: align || 'left',
      ...(typeof typography === 'function' ? typography(cell?.row.original as TData) : typography || {}),
    }),
    [align, typography, cell?.row.original],
  );

  const pin = (cell?.column || header?.column)?.getIsPinned();

  return (
    <TypographyProvider value={mergedTypographyProps}>
      <td
        className={clsx(
          styles.td,
          className,
          isAggregateCell && styles.isAggregateCell,
          isSubRowCell && styles.isSubRowCell,
          match(pin)
            .with('left', () => styles.isPinnedLeft)
            .with('right', () => styles.isPinnedRight)
            .otherwise(() => undefined),
        )}
        {...rest}
        ref={cellRef}
        style={mergedStyles}
      >
        <div
          className={clsx(styles.content, styles[`align-${align || (header?.column?.columnDef as TableColumnDef<TData>)?.align || 'left'}`])}
          ref={ref}
        >
          {cell && (value || showSkeleton || isActionColumn) ? (
            typography ? (
              <Typography
                className={clsx(styles.cellText, styles[`align-${align || (header?.column?.columnDef as TableColumnDef<TData>)?.align || 'left'}`])}
                inheritance="inherited"
                tone={mergedTypographyProps.tone}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Typography>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )
          ) : (
            renderWhenEmpty
          )}
          {children}
        </div>
      </td>
    </TypographyProvider>
  );
}
